"use client";

import { useState, useRef, useEffect } from "react";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import PublicLayout from "@/components/PublicLayout";
import { ShieldCheck, Lock, Mail, ArrowUp } from "lucide-react";
import Link from "next/link";
import { motion } from "framer-motion";
import { usePageContent } from "@/hooks/usePageContent";

type PrivacySection = {
  id: string;
  title: string;
  content: React.ReactNode;
};

export const PRIVACY_COPY: PrivacySection[] = [
  {
    id: "introduction",
    title: "1. Introduction",
    content: (
      <p className="leading-relaxed">
        UniUI is committed to protecting the privacy of its users. This policy
        explains what data we collect, how we use it, and your rights regarding
        your personal information. By using the platform, you agree to the terms
        described herein.
      </p>
    ),
  },
  {
    id: "information-we-collect",
    title: "2. Information We Collect",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>We collect the following categories of information:</p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            <strong>Account information</strong> — name, email, institution,
            department, academic level, and selected courses.
          </li>
          <li>
            <strong>Usage data</strong> — questions asked, materials uploaded,
            study activity, and engagement metrics.
          </li>
          <li>
            <strong>Payment information</strong> — processed via Moniepoint. We
            do not store full card details; all payment data is handled by
            PCI‑DSS compliant processors.
          </li>
          <li>
            <strong>Device information</strong> — IP address, browser type,
            device type, and operating system for security and analytics
            purposes.
          </li>
          <li>
            <strong>Uploaded course materials</strong> — lecture notes, past
            questions, slides, and audio transcripts. These materials are{" "}
            <strong>not private</strong> and are shared with all students
            enrolled in the relevant course to foster collaborative learning.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: "how-we-use-your-information",
    title: "3. How We Use Your Information",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>Your data is used to:</p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            Provide personalised academic answers aligned with your enrolled
            courses.
          </li>
          <li>Track study progress and generate readiness reports.</li>
          <li>
            Facilitate community features such as study groups and discussion
            threads.
          </li>
          <li>Process payments and manage credit allocations.</li>
          <li>Improve the platform through aggregated analytics.</li>
          <li>
            Communicate important updates, exam reminders, and platform
            notifications.
          </li>
          <li>
            <strong>
              Train, refine, and improve our AI models — including the
              retrieval, enrichment, and answer-generation pipelines — using the
              materials you upload and the questions you ask.
            </strong>
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: "use-of-uploaded-materials",
    title: "4. Use of Uploaded Materials",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          Any lecture notes, past questions, slides, audio transcripts, or other
          academic content you upload to UniUI will be available to other
          students enrolled in the same course. These materials are{" "}
          <strong>not private</strong>.
        </p>
        <p>
          By uploading content, you confirm that you have the right to share it
          and you grant UniUI a non‑exclusive, worldwide, royalty‑free licence
          to use, reproduce, modify, and distribute the content for the purpose
          of providing and improving the platform, including training our AI
          models.
        </p>
        <p>
          To protect the academic community from copyright infringement,
          uploaded materials are automatically screened for abusive and
          copyright-infringing content. Materials that violate this policy may
          be removed and the responsible account may be restricted or
          terminated. You may delete your own uploads at any time; however,
          copies already used for AI training may persist.
        </p>
        <p>
          You may delete your uploaded materials at any time. However, copies
          already used for AI training may persist in trained models and cannot
          be retroactively removed.
        </p>
      </div>
    ),
  },
  {
    id: "data-storage-and-security",
    title: "5. Data Storage & Security",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          Your data is stored on secure cloud servers operated by trusted
          infrastructure providers, including:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>Supabase (authentication and database)</li>
          <li>Neon (AI data storage)</li>
          <li>Qdrant (vector search)</li>
          <li>Cloudflare R2 (asset storage)</li>
        </ul>
        <p>
          All data is encrypted in transit (TLS 1.3) and at rest. We implement
          industry‑standard security practices, including regular audits, access
          controls, and vulnerability assessments. Your password is hashed using
          bcrypt and never stored in plain text.
        </p>
      </div>
    ),
  },
  {
    id: "data-sharing",
    title: "6. Data Sharing",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          We do not sell your personal data to third parties. Data sharing
          occurs only in the following circumstances:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            <strong>Uploaded course materials</strong> are shared with other
            students enrolled in the same course to support collaborative
            academic inquiry.
          </li>
          <li>
            <strong>Anonymised, aggregated data</strong> may be used for
            research purposes to improve educational outcomes.
          </li>
          <li>
            <strong>Course representatives</strong> can view anonymised
            engagement statistics for their respective courses.
          </li>
          <li>
            <strong>Legal requirements</strong> — we may disclose information
            when compelled by applicable law, court order, or governmental
            regulation.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: "your-rights",
    title: "7. Your Rights",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>You have the following rights regarding your personal data:</p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            <strong>Access & update</strong> — view and modify your personal
            data at any time via the Settings page.
          </li>
          <li>
            <strong>Export</strong> — download a complete copy of your data in a
            GDPR‑compliant format (JSON/CSV).
          </li>
          <li>
            <strong>Account deletion</strong> — request permanent removal of all
            associated data within 30 days.
          </li>
          <li>
            <strong>Delete uploaded materials</strong> — remove specific uploads
            from the platform. Note that AI models trained on those materials
            cannot be &ldquo;untrained&rdquo; retroactively.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: "cookies-and-tracking",
    title: "8. Cookies & Tracking",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>We use cookies to enhance your experience:</p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            <strong>Essential cookies</strong> — required for authentication,
            session management, and security.
          </li>
          <li>
            <strong>Analytics cookies</strong> — help us understand platform
            usage patterns and performance bottlenecks.
          </li>
        </ul>
        <p>
          You can disable non‑essential cookies in your browser settings.
          Disabling essential cookies may impair platform functionality.
        </p>
      </div>
    ),
  },
  {
    id: "third-party-services",
    title: "9. Third‑Party Services",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          We integrate with the following third‑party services, each with its
          own privacy policy:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            <strong>Supabase</strong> — authentication and database hosting.
          </li>
          <li>
            <strong>Neon</strong> — AI data storage and management.
          </li>
          <li>
            <strong>Qdrant</strong> — vector search and retrieval.
          </li>
          <li>
            <strong>Cloudinary</strong> — image hosting and optimisation.
          </li>
          <li>
            <strong>Cloudflare</strong> — frontend hosting, CDN, and asset
            storage.
          </li>
          <li>
            <strong>Moniepoint</strong> — payment processing and transaction
            verification.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: "childrens-privacy",
    title: "10. Children&apos;s Privacy",
    content: (
      <p className="leading-relaxed">
        UniUI is intended for university students aged 16 and above. We do not
        knowingly collect personal data from children under 16. If we become
        aware of such collection, we will take immediate steps to delete the
        information.
      </p>
    ),
  },
  {
    id: "changes-to-this-policy",
    title: "11. Changes to This Policy",
    content: (
      <p className="leading-relaxed">
        We may update this policy periodically to reflect changes in our
        practices or legal obligations. We will notify users of any material
        changes via email or platform notification. Continued use of the
        platform after changes constitutes acceptance of the revised policy.
      </p>
    ),
  },
  {
    id: "contact-us",
    title: "12. Contact Us",
    content: (
      <p className="leading-relaxed">
        For privacy‑related inquiries, please contact us at{" "}
        <a
          href="mailto:support@uniui.com.ng"
          className="text-y font-bold hover:underline"
        >
          support@uniui.com.ng
        </a>{" "}
        or visit our{" "}
        <Link href="/contact" className="text-y font-bold hover:underline">
          Contact page
        </Link>
        .
      </p>
    ),
  },
];

export default function PrivacyPolicyPage() {
  const [activeSection, setActiveSection] = useState<string>("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const copy = usePageContent("privacy", PRIVACY_COPY);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      {
        rootMargin: "-10% 0px -80% 0px",
      },
    );

    copy.forEach((section) => {
      const ref = sectionRefs.current[section.id];
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let rafId: number;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setSectionRef = (id: string) => (el: HTMLDivElement | null) => {
    sectionRefs.current[id] = el;
  };

  return (
    <PageTransition>
      <PublicLayout>
        <div className="min-h-screen bg-background">
          <div className="max-w-6xl mx-auto px-6 py-12">
            <motion.header
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12 space-y-3"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <ShieldCheck className="w-8 h-8 text-y" />
                <h1 className="display text-4xl md:text-5xl tracking-tight">
                  Privacy Policy
                </h1>
              </div>
              <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest">
                Last updated: August 2026
              </p>
            </motion.header>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-8 space-y-10">
                {copy.map((section, index) => (
                  <motion.section
                    key={section.id}
                    id={section.id}
                    ref={setSectionRef(section.id)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.04 }}
                    className="scroll-mt-24"
                  >
                    <div className="matte-metal p-8 md:p-10 rounded-[2rem] space-y-5">
                      <h2 className="display text-xl md:text-2xl tracking-tight text-foreground">
                        {section.title}
                      </h2>
                      <div className="text-sm text-muted-foreground leading-relaxed font-medium space-y-4">
                        {section.content}
                      </div>
                    </div>
                  </motion.section>
                ))}
              </div>

              <aside className="hidden lg:block lg:col-span-4">
                <div className="sticky top-24 space-y-4">
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground mb-2">
                    Table of Contents
                  </div>
                  <nav className="space-y-1 border-l border-border pl-1">
                    {copy.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => {
                          const ref = sectionRefs.current[section.id];
                          if (ref)
                            ref.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                        }}
                        className={`block w-full text-left py-1.5 pl-3 rounded-lg text-xs font-black uppercase tracking-widest transition-all duration-200 ${
                          activeSection === section.id
                            ? "toc-item-active bg-y/5"
                            : "text-muted-foreground hover:text-foreground hover:translate-x-1"
                        }`}
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>
            </div>
          </div>

          {showBackToTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={scrollToTop}
              className="back-to-top-fab"
              aria-label="Back to top"
            >
              <ArrowUp className="w-5 h-5" strokeWidth={3} />
            </motion.button>
          )}
        </div>
      </PublicLayout>
    </PageTransition>
  );
}
